import React from 'react';
import { Button } from 'reactstrap';

export interface CsvColumn<T> {
    header: string;
    value: (row: T) => string;
}

interface CsvDownloadButtonProps<T> {
    rows: T[];
    columns: CsvColumn<T>[];
    fileNamePrefix: string;
    label: string;
    className?: string;
    delimiter?: string;
}

const CsvDownloadButton = <T,>({
    rows,
    columns,
    fileNamePrefix,
    label,
    className,
    delimiter
}: CsvDownloadButtonProps<T>) => {
    delimiter = delimiter ?? ';';

    const escapeCsv = (value: string) => {
        return `"${value.replace(/"/g, '""')}"`;
    }

    const downloadCsv = () => {
        if (!rows.length || !columns.length) {
            return;
        }

        const header = columns.map(column => escapeCsv(column.header)).join(delimiter);
        const csvRows = rows.map(row => {
            return columns.map(column => escapeCsv(column.value(row) ?? '')).join(delimiter);
        });

        const csv = [header, ...csvRows].join('\n');
        const utf8Bom = '\uFEFF'; //bom helps Excel recognize UTF-8 encoding and doesnt mess up most editors
        const blob = new Blob([utf8Bom, csv], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');

        link.href = url;
        link.setAttribute('download', `${fileNamePrefix}-${timestamp}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }

    return (
        <Button
            color='secondary'
            className={className ?? 'mb-2'}
            onClick={() => downloadCsv()}
        >
            {label}
        </Button>
    );
}

export default CsvDownloadButton;
